// The reconnect policy behind useRoomSocket, kept out of React so it can be
// driven in a test with a fake socket and fake timers.
//
// Why it exists (#124): the hook used to reconnect every 3 s forever, from
// BOTH `onerror` and `onclose` (two timers per failure), and a refused upgrade
// — a 401 on a stale token — looks exactly like the DO hibernating, so one
// abandoned tab with an expired session hit the room socket ~13,400 times in
// 27 hours. This link backs off, retries from one place, asks the session
// whether it is still alive before retrying a connection that never opened,
// and stops when the answer is no or the failures do not stop.

/** The slice of WebSocket the link drives; a test fakes exactly this. */
export interface SocketLike {
  readyState: number;
  send(data: string): void;
  close(): void;
  onopen: ((ev: Event) => void) | null;
  onmessage: ((ev: MessageEvent) => void) | null;
  onclose: ((ev: CloseEvent) => void) | null;
  onerror: ((ev: Event) => void) | null;
}

const SOCKET_OPEN = 1;

export type LinkStatus = "connecting" | "open" | "closed" | "failed";

/**
 * What the session looks like before a retry of a connection that never
 * opened. `alive` is a good token (possibly just refreshed); `gone` means the
 * person is signed out, so the URL the link would open cannot be accepted and
 * retrying is pointless; `unknown` is a client with no session to check — an
 * anonymous listener, or the admin app's shared token.
 */
export type SessionProbe = "alive" | "gone" | "unknown";

export interface RoomSocketLinkOptions {
  /** Open a socket. Called once per attempt so the URL can carry a fresh token. */
  open: () => SocketLike;
  /** Before retrying an attempt that never opened. Default: no session to ask. */
  probe?: () => Promise<SessionProbe>;
  onStatus: (status: LinkStatus) => void;
  onMessage: (data: unknown) => void;
  onOpen?: () => void;
  /** Injected so tests do not depend on Math.random. In [0, 1). */
  random?: () => number;
}

/** First retry delay; doubles on each failure in a row. */
export const RECONNECT_BASE_MS = 3_000;
/** The delay never grows past this. */
export const RECONNECT_MAX_MS = 60_000;
/** Each delay is spread ±25% so tabs that failed together do not retry together. */
export const RECONNECT_JITTER = 0.25;
/** A socket must stay open this long before a close counts as a fresh start. */
export const STEADY_OPEN_MS = 30_000;
/**
 * Attempts in a row that never reached `open` before the link gives up. With
 * the backoff above that is about four and a half minutes of trying; the
 * caller resumes it on a signal that something changed (the tab came back,
 * the network returned).
 */
export const MAX_COLD_FAILURES = 8;
export const KEEPALIVE_MS = 25_000;

/** Delay before the attempt after `failures` consecutive failures, with jitter. */
export function reconnectDelay(failures: number, random: () => number = Math.random): number {
  const exp = Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * 2 ** Math.max(0, failures - 1));
  const spread = (random() * 2 - 1) * RECONNECT_JITTER;
  return Math.round(exp * (1 + spread));
}

export class RoomSocketLink {
  private ws: SocketLike | null = null;
  private retry: ReturnType<typeof setTimeout> | null = null;
  private ping: ReturnType<typeof setInterval> | null = null;
  private stopped = false;
  /** Consecutive attempts that closed without a steady open. */
  private failures = 0;
  /** Attempts in a row that never reached `open` — what MAX_COLD_FAILURES
   * counts. `failures` (the backoff) also counts sockets that opened and
   * dropped early, and seven short sessions then one slow wake-up used to
   * read as eight cold failures and give up in a tab nothing would resume. */
  private coldFailures = 0;
  /** Set when the current attempt opened, cleared when it closes. */
  private openedAt: number | null = null;
  private probing = false;

  constructor(private readonly opts: RoomSocketLinkOptions) {}

  /** Open the first connection. */
  start(): void {
    this.stopped = false;
    this.connect();
  }

  /** Close for good: no socket, no timers, no further status. */
  stop(): void {
    this.stopped = true;
    this.clearTimers();
    const ws = this.ws;
    this.ws = null;
    if (ws) {
      // A close we asked for must not look like a failure.
      ws.onclose = null;
      ws.onerror = null;
      ws.onopen = null;
      ws.onmessage = null;
      ws.close();
    }
  }

  /**
   * Try again now, after the link gave up or while it is waiting out a long
   * delay: the caller saw a reason to expect a different answer (the tab is
   * visible again, the browser is back online). Nothing to do while a socket
   * is open or an attempt is in flight.
   */
  resume(): void {
    if (this.stopped || this.ws || this.probing) return;
    this.clearTimers();
    this.failures = 0;
    this.coldFailures = 0;
    this.connect();
  }

  private clearTimers(): void {
    if (this.retry) clearTimeout(this.retry);
    this.retry = null;
    if (this.ping) clearInterval(this.ping);
    this.ping = null;
  }

  private connect(): void {
    if (this.stopped) return;
    this.retry = null;
    this.openedAt = null;
    this.opts.onStatus("connecting");
    let ws: SocketLike;
    try {
      ws = this.opts.open();
    } catch {
      // A URL the constructor refuses will not get better by itself.
      this.failures = MAX_COLD_FAILURES;
      this.coldFailures = MAX_COLD_FAILURES;
      this.opts.onStatus("failed");
      return;
    }
    this.ws = ws;

    ws.onopen = () => {
      if (this.ws !== ws) return;
      this.openedAt = Date.now();
      this.opts.onStatus("open");
      this.ping = setInterval(() => {
        if (ws.readyState === SOCKET_OPEN) ws.send('{"type":"ping"}');
      }, KEEPALIVE_MS);
      this.opts.onOpen?.();
    };

    ws.onmessage = (event) => {
      if (this.ws !== ws) return;
      this.opts.onMessage(event.data);
    };

    // The browser fires `close` after every `error`, so `error` only has to
    // be observed — scheduling from both is how one failure became two timers.
    ws.onerror = () => {};

    ws.onclose = () => {
      if (this.ws !== ws) return;
      this.ws = null;
      if (this.ping) clearInterval(this.ping);
      this.ping = null;
      const steady = this.openedAt !== null && Date.now() - this.openedAt >= STEADY_OPEN_MS;
      const cold = this.openedAt === null;
      this.openedAt = null;
      this.failures = steady ? 1 : this.failures + 1;
      this.coldFailures = cold ? this.coldFailures + 1 : 0;
      if (this.stopped) return;
      this.opts.onStatus("closed");
      if (cold) void this.retryCold();
      else this.schedule();
    };
  }

  /**
   * The attempt never opened. That is the DO waking slowly, the network gone,
   * or an upgrade the API refused — and the browser does not say which. Ask
   * the session: a refused token can be refreshed, a finished session cannot,
   * and either way a run of these means stop rather than keep knocking.
   */
  private async retryCold(): Promise<void> {
    if (this.coldFailures >= MAX_COLD_FAILURES) {
      this.opts.onStatus("failed");
      return;
    }
    this.probing = true;
    let verdict: SessionProbe = "unknown";
    try {
      verdict = (await this.opts.probe?.()) ?? "unknown";
    } catch {
      // A probe that itself failed says nothing; the backoff still applies.
    } finally {
      this.probing = false;
    }
    if (this.stopped || this.ws) return;
    if (verdict === "gone") {
      // Signed out: this URL will never be accepted. The page learns the same
      // from the session store and will mount a fresh link if it still wants one.
      this.opts.onStatus("failed");
      return;
    }
    this.schedule();
  }

  private schedule(): void {
    if (this.stopped || this.retry) return;
    this.retry = setTimeout(() => this.connect(), reconnectDelay(this.failures, this.opts.random));
  }
}
